// Hlídač — sledovací centrála: hlídaní hráči + Team Season DNA modul + hlídač trhu.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Siren, X, ArrowsClockwise, UsersThree, Dna, Storefront, BellRinging, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import AppShell from "../components/AppShell";
import { MarketWatch } from "../components/MarketWatch";
import { useDnaAnalysis, DnaAnalysisBody } from "../components/SeasonDnaDrawer";
import { useSharedPool } from "../lib/timing-pool-store";
import { useAuth } from "../contexts/AuthContext";
import { getNotifications, markAllNotificationsRead } from "../lib/api";

const ORANGE = "#FF8800";

const relTime = (iso) => {
    try {
        const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
        if (s < 3600) return `před ${Math.max(1, Math.floor(s / 60))} min`;
        if (s < 86400) return `před ${Math.floor(s / 3600)} h`;
        return `před ${Math.floor(s / 86400)} dny`;
    } catch { return ""; }
};

function HlidacFeed() {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const load = () => getNotifications(false, 50).then(setData).catch(() => setData({ items: [], unread_count: 0 }));
    useEffect(() => { if (user) load(); }, [user?.user_id]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <section className="border border-white/10 bg-[#0d0d0d] p-4 space-y-2" data-testid="hlidac-feed">
            <div className="flex items-center gap-2 flex-wrap">
                <h2 className="font-display font-black uppercase text-base flex items-center gap-1.5">
                    <BellRinging size={15} style={{ color: ORANGE }} /> Feed novinek
                </h2>
                {data?.unread_count > 0 && (
                    <>
                        <span className="font-mono text-[9px] px-1.5 py-0.5 bg-red-500 text-white font-bold tabular-nums">{data.unread_count} nových</span>
                        <button type="button" data-testid="hlidac-feed-read-all"
                                onClick={async () => { await markAllNotificationsRead(); load(); }}
                                className="ml-auto font-mono text-[9px] uppercase tracking-widest text-white/50 hover:text-fa-mint flex items-center gap-1">
                            <CheckCircle size={11} /> Označit přečtené
                        </button>
                    </>
                )}
            </div>
            {!user ? (
                <p className="font-mono text-[11px] text-white/50 py-2">Přihlas se, ať ti Hlídač může posílat novinky o tvých hráčích.</p>
            ) : !data ? (
                <p className="font-mono text-[11px] text-white/40 py-2">Načítám…</p>
            ) : data.items.length === 0 ? (
                <p className="font-mono text-[11px] text-white/50 py-2" data-testid="hlidac-feed-empty">
                    Zatím žádné novinky — přidej hráče do Hlídače a ozvu se, jakmile se něco stane.
                </p>
            ) : (
                <div className="divide-y divide-white/5" data-testid="hlidac-feed-list">
                    {data.items.map((it, i) => {
                        const inner = (
                            <>
                                <span className={`mt-1.5 w-1.5 h-1.5 shrink-0 ${it.read ? "bg-white/20" : "bg-[#FF8800]"}`} />
                                <span className="flex-1">
                                    <span className={`font-mono text-sm leading-snug block ${it.read ? "text-white/60" : "text-white"}`}>{it.text}</span>
                                    <span className="font-mono text-[11px] uppercase tracking-widest text-white/40">
                                        {relTime(it.created_at)}{it.player_id != null && <span className="text-fa-mint/70 ml-2">profil hráče →</span>}
                                    </span>
                                </span>
                            </>
                        );
                        return it.player_id != null ? (
                            <Link key={i} to={`/players/${it.player_id}`} data-testid={`hlidac-feed-item-${i}`}
                                  className="flex items-start gap-2 py-2 hover:bg-white/5 px-1 -mx-1">
                                {inner}
                            </Link>
                        ) : (
                            <div key={i} className="flex items-start gap-2 py-2 px-1 -mx-1" data-testid={`hlidac-feed-item-${i}`}>{inner}</div>
                        );
                    })}
                </div>
            )}
        </section>
    );
}

export default function Hlidac() {
    const { pool, totalPrice, removePlayer } = useSharedPool();
    const { analysis, busy, analyze } = useDnaAnalysis(pool, true);

    return (
        <AppShell>
            <div className="space-y-8" data-testid="hlidac-page">
                <header className="border-l-4 pl-4" style={{ borderColor: ORANGE }}>
                    <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest" style={{ color: ORANGE }}>
                        <Siren size={14} weight="fill" /> Sledovací centrála
                    </div>
                    <h1 className="font-display font-black uppercase tracking-tight text-3xl md:text-4xl mt-1">Hlídač</h1>
                    <p className="font-mono text-[11px] text-white/55 mt-1 max-w-2xl">
                        Hlídá tvoje hráče, jejich sezónní DNA i celý trh. Přidávej hráče tlačítkem „Do Hlídače" na profilech a ve finderech.
                    </p>
                </header>

                {/* Feed novinek */}
                <HlidacFeed />

                {/* Hlídaní hráči */}
                <section className="border border-white/10 bg-[#0d0d0d] p-4 space-y-2" data-testid="hlidac-players">
                    <div className="flex items-center gap-2 flex-wrap">
                        <h2 className="font-display font-black uppercase text-base flex items-center gap-1.5">
                            <UsersThree size={15} style={{ color: ORANGE }} /> Hlídaní hráči
                        </h2>
                        <span className="font-mono text-[10px] text-white/40 tabular-nums">{pool.length} hráčů · £{totalPrice.toFixed(1)}m</span>
                    </div>
                    {pool.length === 0 ? (
                        <p className="font-mono text-[11px] text-white/50 py-4" data-testid="hlidac-empty">
                            Zatím nikoho nehlídáš. Otevři <Link to="/players" className="text-fa-mint hover:underline">profil hráče</Link> nebo
                            kterýkoli finder a klikni na <span style={{ color: ORANGE }}>„Do Hlídače"</span>.
                        </p>
                    ) : (
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-1.5">
                            {pool.map((p) => (
                                <div key={p.fpl_id} className="border border-white/10 bg-black/30 px-3 py-2 flex items-center gap-2"
                                     data-testid={`hlidac-player-${p.fpl_id}`}>
                                    <Link to={`/players/${p.fpl_id}`} className="font-display font-bold text-sm hover:text-fa-mint truncate flex-1">
                                        {p.display_name}
                                    </Link>
                                    <span className="font-mono text-[9px] text-white/45">{p.team} · {p.position}</span>
                                    <span className="font-mono text-[10px] text-white/70 tabular-nums">{p.current_price == null ? "£—" : `£${p.current_price.toFixed(1)}`}</span>
                                    <button type="button" data-testid={`hlidac-remove-${p.fpl_id}`}
                                            onClick={() => { removePlayer(p.fpl_id); toast.success(`${p.display_name} odebrán z Hlídače`); }}
                                            className="p-1 text-white/35 hover:text-red-400">
                                        <X size={12} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </section>

                {/* Modul: Team Season DNA */}
                <section className="border border-fa-yellow/25 bg-fa-yellow/[0.03] p-4 space-y-3" data-testid="hlidac-dna">
                    <div className="flex items-center gap-2 flex-wrap">
                        <h2 className="font-display font-black uppercase text-base flex items-center gap-1.5">
                            <Dna size={15} className="text-fa-yellow" /> Team Season DNA
                        </h2>
                        <span className="font-mono text-[9px] uppercase tracking-widest text-white/40">analytický modul Hlídače</span>
                        <button type="button" onClick={() => analyze(true)} disabled={busy || pool.length < 5}
                                data-testid="hlidac-dna-refresh"
                                className="ml-auto font-mono text-[9px] uppercase tracking-widest px-3 py-1.5 border border-fa-yellow/40 text-fa-yellow hover:bg-fa-yellow hover:text-black disabled:opacity-30 flex items-center gap-1.5">
                            <ArrowsClockwise size={11} className={busy ? "animate-spin" : ""} /> Přepočítat
                        </button>
                    </div>
                    <div className="space-y-4 max-w-3xl">
                        <DnaAnalysisBody pool={pool} analysis={analysis} busy={busy} />
                    </div>
                    <Link to="/tools/season-dna" className="inline-block font-mono text-[9px] uppercase tracking-widest text-white/40 hover:text-fa-yellow">
                        Celý Season DNA nástroj (pokročilé) →
                    </Link>
                </section>

                {/* Modul: Hlídač trhu */}
                <section className="space-y-3" data-testid="hlidac-market">
                    <div className="flex items-center gap-2 flex-wrap">
                        <h2 className="font-display font-black uppercase text-base flex items-center gap-1.5">
                            <Storefront size={15} style={{ color: ORANGE }} /> Hlídač trhu
                        </h2>
                        <span className="font-mono text-[9px] uppercase tracking-widest text-white/40">pasti a příležitosti z Prediction Modelu</span>
                    </div>
                    <MarketWatch />
                </section>
            </div>
        </AppShell>
    );
}
